/// <amd-module name="VSS/Features/MarkdownEditor/Components/MarkdownToolbar" />
import * as React from "react";
import { ObservableCollection, ObservableLike } from "VSS/Core/Observable";
import { getMenuItems, IContributedMenuItem } from "VSS/Features/PlatformUI/ContributedMenu";
import { useComponentContext } from "VSS/Platform/Layout";
import { HeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import { IMarkdownToolbarCommand } from "../Commands/Toolbar";
import { applyTextFormatting } from "../Formatting/Common";
import { IMarkdownToolbarProps } from "./MarkdownToolbar.Props";

export const MarkdownToolbar = (props: IMarkdownToolbarProps): JSX.Element => {
    const { className, contributionContext, contributionId, getTextSource } = props;

    const contributedItems = new ObservableCollection<IContributedMenuItem>();
    if (contributionId) {
        const pageContext = useComponentContext().pageContext;
        contributedItems.push(getMenuItems(pageContext, contributionId, contributionContext));
    }

    const onActivateHandler = (command: IMarkdownToolbarCommand) => {
        const textSource = getTextSource && getTextSource();
        if (textSource && command.formatText) {
            // Get args from the text source first to get the text to apply markdown formatting
            const formattingArgs = textSource.getFormattingArgs();

            // Perform formatting based on the activated command
            const formattingResult = formattingArgs && command.formatText(formattingArgs, e => textSource.showError(e));

            // If there is a formatting result produced from the activated command, apply it on the text source
            applyTextFormatting(textSource, formattingResult);
        }
    };

    const attachActivateHandler = (command: IMarkdownToolbarCommand) => {
        if (!command.onActivate && command.formatText) {
            command.onActivate = onActivateHandler;
        }
    };

    const processToolbarCommand = (command: IMarkdownToolbarCommand) => {
        // Attach activate handler first
        attachActivateHandler(command);

        // Handle the sub menu props by cloning the sub items and attaching
        // activate handler to each of them.
        // We don't support observable for child items (since we don't support it in IMarkdownToolbarProps either)
        if (command.subMenuProps && command.subMenuProps.items) {
            const subToolbarCommands: IMarkdownToolbarCommand[] = [];
            for (const subCommand of ObservableLike.getValue(command.subMenuProps.items) || []) {
                // We need to clone sub items too
                const subToolbarCommand = { ...subCommand } as IMarkdownToolbarCommand;

                // Clone sub menu props
                if (subCommand.subMenuProps) {
                    subToolbarCommand.subMenuProps = { ...subCommand.subMenuProps };
                }

                // Process cloned sub command
                processToolbarCommand(subToolbarCommand);
                subToolbarCommands.push(subToolbarCommand);
            }

            command.subMenuProps.items = subToolbarCommands;
        }
    };

    return (
        <Observer contributedItems={contributedItems}>
            {(observerProps: { contributedItems: IContributedMenuItem[] }) => {
                const contributedItems = observerProps.contributedItems.sort(
                    (ci1, ci2) => (ci1.rank || Number.MAX_VALUE) - (ci2.rank || Number.MAX_VALUE)
                );            
                const sortedCommands: IMarkdownToolbarCommand[] = (props.commands !== undefined && props.commands !== null) ? 
                    props.commands.sort(
                        (ci1, ci2) => (ci1.order || Number.MAX_VALUE) - (ci2.order || Number.MAX_VALUE)
                    ) : 
                    [];

                const commands: IMarkdownToolbarCommand[] = [...sortedCommands, ...contributedItems];
                // To mimic the editor toolbar we have today, we apply no-margin style to all commands and make them subtle.
                const items = commands.map(command => {
                    const toolbarCommand = {
                        ...command,
                        ariaLabel: command.text || command.tooltipProps?.text,
                        className: "no-margin",
                        important: true,
                        subtle: true,
                        text: undefined,
                        tooltipProps: command.tooltipProps || { text: command.text }
                    } as IMarkdownToolbarCommand;

                    // Clone sub menu props
                    if (command.subMenuProps) {
                        toolbarCommand.subMenuProps = { ...command.subMenuProps };
                    }

                    processToolbarCommand(toolbarCommand);

                    return toolbarCommand;
                });

                return (
                    <HeaderCommandBar
                        className={css(className, "markdown-toolbar flex-row flex-grow flex-wrap rhythm-horizontal-8 no-margin")}
                        items={items}
                    />
                );
            }}
        </Observer>
    );
};
